import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./Select.css";

export type SelectChangeEvent = { target: { value: string; name?: string } };

export type SelectProps = {
  /** sm 32px, md 40px (the default, lines up with Input and Button) */
  size?: "sm" | "md";
  /** Full width of its container */
  block?: boolean;
  /** Red border: the field has an error */
  invalid?: boolean;
  value: string | number;
  onChange: (event: SelectChangeEvent) => void;
  children?: React.ReactNode;
  className?: string;
  id?: string;
  name?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
  placeholder?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
};

type Item = { value: string; label: string; disabled: boolean };

const textOf = (node: React.ReactNode): string => {
  if (node === null || node === undefined || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(textOf).join("");
  if (React.isValidElement(node)) return textOf((node.props as { children?: React.ReactNode }).children);
  return "";
};

const collect = (children: React.ReactNode, out: Item[]) => {
  React.Children.toArray(children).forEach((child) => {
    if (!React.isValidElement(child)) return;
    if (child.type === React.Fragment) {
      collect((child.props as { children?: React.ReactNode }).children, out);
      return;
    }
    if (child.type === "option") {
      const p = child.props as { value?: string | number; disabled?: boolean; children?: React.ReactNode };
      const label = textOf(p.children);
      out.push({ value: p.value === undefined ? label : String(p.value), label, disabled: !!p.disabled });
    }
  });
  return out;
};

const MAX_HEIGHT = 320;
/** How many options Page Up / Page Down jump. */
const PAGE = 10;
/** Typed letters add up into one search until the keyboard has been quiet this long. */
const TYPE_AHEAD_MS = 600;

/*
 * The brand dropdown, a drop-in for a native <select> (same value / onChange(e) / <option> children; ported from the
 * website's ClashSelect). A select-only combobox (the WAI-ARIA pattern): the trigger keeps the focus while the list is open and takes every
 * key; aria-activedescendant points screen readers at the option under the keyboard, and the trigger's text is the
 * chosen value (a combobox is named by its label, not by its text). The list never takes focus.
 */
const Select = ({
  size = "md",
  block = false,
  invalid = false,
  value,
  onChange,
  children,
  className,
  id,
  name,
  disabled,
  style,
  placeholder,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
}: SelectProps) => {
  const items = useMemo(() => collect(children, []), [children]);
  const current = String(value);
  const selectedIndex = items.findIndex((i) => i.value === current);
  const uid = useId();
  const listId = `${uid}-list`;
  const optionId = (index: number) => `${listId}-${index}`;

  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [pos, setPos] = useState<React.CSSProperties>({ visibility: "hidden" });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const typed = useRef({ text: "", timer: 0 as number | undefined });

  const firstEnabled = useCallback(() => items.findIndex((i) => !i.disabled), [items]);
  const lastEnabled = useCallback(() => {
    for (let i = items.length - 1; i >= 0; i--) if (!items[i].disabled) return i;
    return -1;
  }, [items]);

  const openList = (at?: number) => {
    if (disabled) return;
    setActive(at ?? (selectedIndex >= 0 && !items[selectedIndex].disabled ? selectedIndex : firstEnabled()));
    setPos({ visibility: "hidden" });
    setOpen(true);
  };

  const close = useCallback((refocus: boolean) => {
    setOpen(false);
    window.clearTimeout(typed.current.timer);
    typed.current.text = "";
    if (refocus) triggerRef.current?.focus();
  }, []);

  const choose = (index: number) => {
    const item = items[index];
    if (!item || item.disabled) return;
    if (item.value !== current) onChange({ target: { value: item.value, name } });
    close(true);
  };

  // The enabled option `steps` enabled options away from `from` (stops at the last one in that direction).
  const move = (from: number, dir: 1 | -1, steps = 1) => {
    let to = from;
    let left = steps;
    for (let i = from + dir; i >= 0 && i < items.length && left > 0; i += dir) {
      if (!items[i].disabled) {
        to = i;
        left -= 1;
      }
    }
    return to;
  };

  // Type-ahead: the next enabled option starting with the letters typed so far. A first letter starts after the
  // current option, so typing it again cycles through the options with that letter.
  const typeAhead = (char: string): number | undefined => {
    const t = typed.current;
    window.clearTimeout(t.timer);
    t.text += char.toLowerCase();
    t.timer = window.setTimeout(() => (t.text = ""), TYPE_AHEAD_MS);
    const from = open ? active : selectedIndex;
    const start = Math.max(from, -1) + (t.text.length > 1 ? 0 : 1);
    const n = items.length;
    for (let i = 0; i < n; i++) {
      const index = (((start + i) % n) + n) % n;
      if (!items[index].disabled && items[index].label.toLowerCase().startsWith(t.text)) return index;
    }
    return undefined;
  };

  // Place the list under the trigger, or above it when there is no room below, and keep all of it on screen: it is
  // as wide as its longest option (at least the trigger, at most the screen less 8px a side).
  useLayoutEffect(() => {
    if (!open || !triggerRef.current || !listRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const h = Math.min(listRef.current.scrollHeight, MAX_HEIGHT);
    const below = window.innerHeight - r.bottom - 8;
    const above = r.top - 8;
    const flip = below < h && above > below;
    const room = Math.max(120, Math.min(MAX_HEIGHT, flip ? above : below));
    const width = Math.max(r.width, 0);
    const listWidth = Math.min(Math.max(listRef.current.offsetWidth, width), window.innerWidth - 16);
    const left = Math.max(8, Math.min(r.left, window.innerWidth - listWidth - 8));
    setPos({
      left,
      minWidth: width,
      maxHeight: room,
      ...(flip ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }),
    });
  }, [open]);

  // Dismissal: outside press, resize, scroll of anything outside the list.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: Event) => {
      const t = e.target as Node;
      if (listRef.current?.contains(t) || triggerRef.current?.contains(t)) return;
      close(false);
    };
    const onScroll = (e: Event) => {
      if (listRef.current && e.target instanceof Node && listRef.current.contains(e.target)) return;
      close(false);
    };
    // Height-only resizes (mobile address bar) must not dismiss the list.
    const startWidth = window.innerWidth;
    const onResize = () => {
      if (window.innerWidth !== startWidth) close(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open, close]);

  // Keep the active option visible.
  useEffect(() => {
    if (!open || active < 0) return;
    const el = listRef.current?.children[active] as HTMLElement | undefined;
    if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "nearest" });
  }, [open, active]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    const printable = e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;
    // A space in the middle of typing a name ("united s…") is part of the name
    if (printable && (e.key !== " " || typed.current.text)) {
      e.preventDefault();
      const hit = typeAhead(e.key);
      if (hit === undefined) return;
      if (open) setActive(hit);
      else openList(hit);
      return;
    }

    if (!open) {
      if (["ArrowDown", "ArrowUp", "Enter", " "].includes(e.key)) {
        e.preventDefault();
        openList();
      } else if (e.key === "Home" || e.key === "End") {
        e.preventDefault();
        openList(e.key === "Home" ? firstEnabled() : lastEnabled());
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActive((a) => (a < 0 ? firstEnabled() : move(a, 1)));
        break;
      case "ArrowUp":
        e.preventDefault();
        if (e.altKey) choose(active);
        else setActive((a) => (a < 0 ? lastEnabled() : move(a, -1)));
        break;
      case "PageDown":
        e.preventDefault();
        setActive((a) => (a < 0 ? firstEnabled() : move(a, 1, PAGE)));
        break;
      case "PageUp":
        e.preventDefault();
        setActive((a) => (a < 0 ? lastEnabled() : move(a, -1, PAGE)));
        break;
      case "Home":
        e.preventDefault();
        setActive(firstEnabled());
        break;
      case "End":
        e.preventDefault();
        setActive(lastEnabled());
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        choose(active);
        break;
      case "Escape":
        // Closes the list only, not a dialog it sits in
        e.preventDefault();
        e.stopPropagation();
        close(true);
        break;
      case "Tab":
        // The focus moves on as usual
        close(false);
        break;
    }
  };

  useEffect(() => () => window.clearTimeout(typed.current.timer), []);

  const label = selectedIndex >= 0 ? items[selectedIndex].label : placeholder || "";
  const showingPlaceholder = selectedIndex < 0;

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        id={id}
        name={name}
        disabled={disabled}
        style={style}
        className={`clash-select clash-select--${size}${block ? " clash-select--block" : ""}${invalid ? " is-invalid" : ""}${open ? " is-open" : ""}${className ? ` ${className}` : ""}`}
        aria-invalid={invalid || undefined}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open && active >= 0 ? optionId(active) : undefined}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        onClick={() => (open ? close(false) : openList())}
        onKeyDown={onKeyDown}
        onBlur={(e) => {
          // The focus left for something else (not a press on the list, which keeps it here)
          if (open && !listRef.current?.contains(e.relatedTarget as Node | null)) close(false);
        }}
      >
        <span className={`clash-select__label${showingPlaceholder ? " is-placeholder" : ""}`}>
          {label || " "}
        </span>
        <svg className="clash-select__chevron" width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
          <path d="M2 4l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" />
        </svg>
      </button>
      {open &&
        createPortal(
          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            className="clash-select__list"
            style={pos}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            // A press on the list keeps the focus on the trigger
            onMouseDown={(e) => e.preventDefault()}
          >
            {items.map((item, i) => (
              <li
                key={`${item.value}-${i}`}
                id={optionId(i)}
                role="option"
                // The option under the keyboard is the one a screen reader announces as selected; Enter commits it
                aria-selected={i === active}
                aria-disabled={item.disabled || undefined}
                className={`clash-select__option${i === active ? " is-active" : ""}${
                  i === selectedIndex ? " is-selected" : ""
                }${item.disabled ? " is-disabled" : ""}`}
                onMouseMove={() => !item.disabled && active !== i && setActive(i)}
                onClick={() => choose(i)}
              >
                <span>{item.label}</span>
                {i === selectedIndex && (
                  <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                    <path d="M2 7.5l3.5 3.5L12 3.5" fill="none" stroke="currentColor" strokeWidth="2" />
                  </svg>
                )}
              </li>
            ))}
          </ul>,
          document.body
        )}
    </>
  );
};

export default Select;
